import { useCallback, useEffect, useRef, useState } from 'react';
import usePersistedKeyValueStore from '../usePersistedKeyValueStore';
import useFastenConnections from './useFastenConnections';

const STORAGE_CATEGORY = 'fasten-pending-connections';
const POLLING_INTERVAL_MS = 15_000; // 15 seconds
const MAX_POLLING_DURATION_MS = 180_000; // 3 minutes
// Give up on a pending connection the webhook never confirms. Generously above the polling duration so a
// slow webhook still resolves, but bounded (and based on the persisted timestamp) so a failed connection
// can't leave a stale card that restarts polling on every remount/app restart — the in-memory timeout
// flag does not survive those.
const MAX_PENDING_AGE_MS = 10 * 60 * 1000; // 10 minutes

export type PendingFastenConnection = {
    org_connection_id: string;
    platform_type: string;
    brand_id: string;
    brand_name: string;
    connection_status: string;
    medicalProfileId: string;
    timestamp: number;
    loading_status: 'pending';
};

export type UsePendingFastenConnectionsArgs = {
    medicalProfileId: string;
    enabled?: boolean;
};

export type UsePendingFastenConnectionsResult = {
    pendingConnections: PendingFastenConnection[];
    isPolling: boolean;
    addPendingConnection: (connection: Omit<PendingFastenConnection, 'timestamp' | 'loading_status'>) => Promise<void>;
    /** Registers several connections in one write; sequential single adds would overwrite each other. */
    addPendingConnections: (
        connections: Omit<PendingFastenConnection, 'timestamp' | 'loading_status'>[],
    ) => Promise<void>;
    clearPendingConnections: () => Promise<void>;
    timeRemainingSeconds: number;
};

const usePendingFastenConnections = ({
    medicalProfileId,
    enabled = true,
}: UsePendingFastenConnectionsArgs): UsePendingFastenConnectionsResult => {
    // Persisted storage for pending connections
    const {
        value: pendingMap,
        setValue: setPendingMap,
        isLoading: isLoadingStorage,
    } = usePersistedKeyValueStore<Record<string, PendingFastenConnection>>({
        category: STORAGE_CATEGORY,
        key: medicalProfileId,
        defaultValue: {},
    });

    // Backend connections
    const { connections: backendConnections, refetch } = useFastenConnections({
        medicalProfileId,
        enabled,
    });

    // Polling state
    const [isPolling, setIsPolling] = useState(false);
    const [timeRemainingSeconds, setTimeRemainingSeconds] = useState(0);
    const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const timeUpdateIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const pollingStartTimeRef = useRef<number | null>(null);
    // Track if polling has timed out to prevent automatic restart
    const hasTimedOutRef = useRef(false);

    // Convert map to array for this profile
    const pendingConnections = Object.values(pendingMap || {}).filter(
        (conn) => conn.medicalProfileId === medicalProfileId,
    );

    // Add pending connections. One write for the whole batch: each write starts from the map this render saw,
    // so adding several connections one at a time would keep only the last.
    const addPendingConnections = useCallback(
        async (connections: Omit<PendingFastenConnection, 'timestamp' | 'loading_status'>[]) => {
            if (connections.length === 0) return;
            const timestamp = Date.now();
            const newConnections = Object.fromEntries(
                connections.map((connection) => [
                    connection.org_connection_id,
                    { ...connection, timestamp, loading_status: 'pending' as const },
                ]),
            );

            // Reset timeout flag when adding a new connection so polling can start
            hasTimedOutRef.current = false;

            await setPendingMap({
                ...(pendingMap || {}),
                ...newConnections,
            });
        },
        [pendingMap, setPendingMap],
    );

    const addPendingConnection = useCallback(
        (connection: Omit<PendingFastenConnection, 'timestamp' | 'loading_status'>) =>
            addPendingConnections([connection]),
        [addPendingConnections],
    );

    // Clear all pending connections for this profile
    const clearPendingConnections = useCallback(async () => {
        await setPendingMap({});
    }, [setPendingMap]);

    // Stop polling
    const stopPolling = useCallback((timedOut = false) => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
        if (timeUpdateIntervalRef.current) {
            clearInterval(timeUpdateIntervalRef.current);
            timeUpdateIntervalRef.current = null;
        }
        setIsPolling(false);
        pollingStartTimeRef.current = null;
        setTimeRemainingSeconds(0);
        if (timedOut) {
            // Keep the pending card on timeout rather than clearing it: a slow webhook can still
            // confirm the connection afterwards, and the reconcile effect removes the card once the
            // backend reports it. Clearing here made a just-connected portal vanish from the list.
            // Only stop the poller from auto-restarting.
            hasTimedOutRef.current = true;
        }
    }, []);

    // Start polling
    const startPolling = useCallback(() => {
        if (pollingIntervalRef.current) return; // Already polling

        const startTime = Date.now();
        pollingStartTimeRef.current = startTime;
        setIsPolling(true);
        setTimeRemainingSeconds(Math.ceil(MAX_POLLING_DURATION_MS / 1000));

        // Polling interval - fetch backend connections
        pollingIntervalRef.current = setInterval(async () => {
            const elapsed = Date.now() - startTime;

            // Check if max duration reached
            if (elapsed >= MAX_POLLING_DURATION_MS) {
                stopPolling(true); // Mark as timed out
                return;
            }

            // Fetch latest connections from backend
            await refetch();
        }, POLLING_INTERVAL_MS);

        // Time remaining update interval (every second)
        timeUpdateIntervalRef.current = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const remaining = Math.max(0, Math.ceil((MAX_POLLING_DURATION_MS - elapsed) / 1000));
            setTimeRemainingSeconds(remaining);

            if (remaining <= 0) {
                stopPolling(true); // Mark as timed out
            }
        }, 1000);
    }, [refetch, stopPolling]);

    // Effect: Reconcile pending with backend connections when backend data changes
    useEffect(() => {
        if (!pendingMap || Object.keys(pendingMap).length === 0) {
            // No pending items, stop polling if running
            if (isPolling) {
                stopPolling();
            }
            return;
        }

        const backendConnectionsByOrgId = new Map(backendConnections.map((c) => [c.orgConnectionId, c]));
        const updatedMap = { ...pendingMap };
        let hasChanges = false;

        for (const orgId of Object.keys(pendingMap)) {
            const backendConnection = backendConnectionsByOrgId.get(orgId);
            const pending = pendingMap[orgId];
            // Remove pending if the backend now has it AND it's not still revoked (reconnect reuses the
            // same org_connection_id, so we wait until the webhook flips 'revoked' -> 'authorized')...
            const isConfirmed = !!backendConnection && backendConnection.connectionStatus !== 'revoked';
            // ...or if it has been pending too long, so a connection the webhook never confirms doesn't
            // leave a stale card that restarts polling on every remount.
            const isExpired = !!pending && Date.now() - pending.timestamp > MAX_PENDING_AGE_MS;
            if (isConfirmed || isExpired) {
                delete updatedMap[orgId];
                hasChanges = true;
            }
        }

        if (hasChanges) {
            setPendingMap(updatedMap);
        }

        // If all pending items are reconciled, stop polling
        const remainingPending = Object.keys(updatedMap).filter(
            (key) => updatedMap[key]?.medicalProfileId === medicalProfileId,
        );
        if (remainingPending.length === 0 && isPolling) {
            stopPolling();
        }
    }, [backendConnections, pendingMap, setPendingMap, isPolling, stopPolling, medicalProfileId]);

    // Effect: Start polling on mount if there are pending items
    useEffect(() => {
        // Don't restart polling if it has already timed out
        if (!isLoadingStorage && pendingConnections.length > 0 && !isPolling && !hasTimedOutRef.current) {
            startPolling();
        }
    }, [isLoadingStorage, pendingConnections.length, isPolling, startPolling]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
            if (timeUpdateIntervalRef.current) {
                clearInterval(timeUpdateIntervalRef.current);
            }
        };
    }, []);

    return {
        pendingConnections,
        isPolling,
        addPendingConnection,
        addPendingConnections,
        clearPendingConnections,
        timeRemainingSeconds,
    };
};

export default usePendingFastenConnections;
